import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, AlertCircle } from "lucide-react";
import { agencyApi } from "./agencyApi";
import { StatusPill, Lifecycle, greeting, firstName, when, isDelivered, stageChipLabel, AgencyProducerCard } from "./AgencyBits";

export default function AgencyHome() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [pm, setPm] = useState(null);

  useEffect(() => {
    agencyApi.home().then((r) => setData(r.data)).catch(() => setErr("We couldn't load your home right now."));
    agencyApi.pmContact().then((r) => setPm(r.data.pm || null)).catch(() => {});
  }, []);

  if (err) return <div className="ag-empty" data-testid="agency-home-error">{err}</div>;
  if (!data) return <div className="ag-small">Loading…</div>;

  const { agency, needs_attention = [], active_projects = [], recent_activity = [] } = data;
  const delivered = active_projects.filter((p) => isDelivered(p.stage));
  const active = active_projects.filter((p) => !isDelivered(p.stage));

  return (
    <div data-testid="agency-home" className="ag-home-layout">
      <div style={{ minWidth: 0 }}>
        <p className="ag-eyebrow ag-entrance">NJM Productions · Agency Portal</p>
        <h1 className="ag-h1 ag-entrance" style={{ margin: "10px 0 40px" }}>{greeting()}, {firstName(agency?.name)}.</h1>

        <section className="ag-section ag-entrance-2">
          <h2 className="ag-eyebrow" style={{ marginBottom: 16 }}>Needs your attention</h2>
          {needs_attention.length === 0 ? (
            <div className="ag-empty" data-testid="agency-home-noattention">Nothing needs you right now. You're all caught up.</div>
          ) : (
            <div className="ag-stack">
              {needs_attention.map((n) => (
                <button key={n.id} className="ag-card ag-card-hover" style={{ display: "flex", alignItems: "center", gap: 16, width: "100%", textAlign: "left" }}
                  onClick={() => nav(n.recce_id ? `/agency/projects/${n.project_id}?tab=Recce&recce=${n.recce_id}` : `/agency/projects/${n.project_id}`)}
                  data-testid="agency-needs-item">
                  <AlertCircle size={20} color="#C0392B" strokeWidth={1.75} />
                  <div style={{ flex: 1 }}>
                    <div className="ag-h3" style={{ fontSize: 16 }}>{n.title}</div>
                    <div className="ag-small">{n.project_name}{n.due ? ` · due ${when(n.due)}` : ""}</div>
                  </div>
                  <StatusPill status={n.status} />
                  <ArrowRight size={18} color="#8A8A8A" />
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="ag-section ag-entrance-3">
          <h2 className="ag-eyebrow" style={{ marginBottom: 16 }}>Active productions</h2>
          {active.length === 0 ? (
            <div className="ag-empty">No active productions yet.</div>
          ) : (
            <div className="ag-grid ag-grid-2">
              {active.map((p) => (
                <button key={p.id} className="ag-card ag-card-hover" style={{ textAlign: "left" }}
                  onClick={() => nav(`/agency/projects/${p.id}`)} data-testid="agency-project-card">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 18 }}>
                    <div className="ag-h3">{p.name}</div>
                    <StatusPill status={stageChipLabel(p.stage, p.lost)} />
                  </div>
                  <Lifecycle timeline={p.timeline} />
                  <div style={{ display: "flex", gap: 28, marginTop: 22, paddingTop: 18, borderTop: "1px solid var(--line)" }}>
                    <Metric label="Your NJM lead" value={p.pm || "Being assigned"} />
                    <Metric label="Open items" value={String(p.open_items_count)} />
                    <Metric label="Crew" value={`${p.crew_confirmed}/${p.crew_total} confirmed`} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {delivered.length > 0 && (
          <section className="ag-delivered-section" data-testid="agency-home-delivered">
            <h2 className="ag-eyebrow" style={{ marginBottom: 16 }}>Delivered</h2>
            {delivered.map((p) => (
              <div key={p.id} className="ag-delivered-row" data-testid="agency-delivered-row">
                <div className="ag-h3" style={{ fontSize: 15 }}>{p.name}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <StatusPill status={stageChipLabel(p.stage, p.lost)} />
                  <button className="ag-link" onClick={() => nav(`/agency/projects/${p.id}`)} data-testid="agency-delivered-view">VIEW →</button>
                </div>
              </div>
            ))}
          </section>
        )}

        {recent_activity.length > 0 && (
          <section className="ag-section">
            <h2 className="ag-eyebrow" style={{ marginBottom: 16 }}>Latest activity</h2>
            <div className="ag-card">
              {recent_activity.map((a, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: i < recent_activity.length - 1 ? "1px solid var(--line)" : "none" }}>
                  <span className="ag-body" style={{ fontSize: 13 }}>{a.text} <span className="ag-small">· {a.project_name}</span></span>
                  <span className="ag-small">{when(a.at)}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <AgencyProducerCard pm={pm} sticky />
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="ag-label" style={{ marginBottom: 5 }}>{label}</div>
      <div className="ag-body" style={{ fontSize: 13, fontWeight: 600 }}>{value}</div>
    </div>
  );
}
